import { formatKg, kgToG, type InventoryRecord, type StockRow } from '@pms/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useLiveQuery } from 'dexie-react-hooks';

import { request } from '../../sync/api.js';
import { db } from '../../sync/db.js';

/**
 * Остатки сырья: с чего начинали и что насчитали руками.
 *
 * Прямой правки остатка здесь нет. Остаток — сумма движений, и менять его
 * можно только тем, что объяснимо: первичным вводом или инвентаризацией.
 * Иначе через месяц никто не скажет, откуда взялась разница в тонну.
 *
 * На экране килограммы, в базе граммы: дробей в учёте не бывает.
 */

interface StockResponse {
  stock: StockRow[];
  inventories: InventoryRecord[];
}

type Mode = 'none' | 'opening' | 'inventory';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function StockScreen(): JSX.Element {
  const [data, setData] = useState<StockResponse | null>(null);
  const [mode, setMode] = useState<Mode>('none');
  const [at, setAt] = useState(today());
  const [note, setNote] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  /** Готовая продукция считается в тот же заход, штуками. */
  const [productValues, setProductValues] = useState<Record<string, string>>({});
  const products = useLiveQuery(() => db.productSizes.orderBy('sortOrder').toArray(), [], []);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await request<StockResponse>('/api/stock', { method: 'GET' }, 20_000));
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить остатки');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const start = (next: Mode): void => {
    setMode(next);
    setNotice(null);
    setError(null);
    setAt(today());
    setNote('');
    // Заполняем текущими значениями: инвентаризация обычно подтверждает
    // большинство строк, и перебивать их все руками незачем
    const prefill: Record<string, string> = {};
    for (const row of data?.stock ?? []) {
      prefill[row.materialTypeId] = next === 'inventory' ? String(row.qtyG / 1000) : '';
    }
    setValues(prefill);

    const productPrefill: Record<string, string> = {};
    if (next === 'inventory') {
      for (const size of products) productPrefill[size.id] = '';
    }
    setProductValues(productPrefill);
  };

  const submit = async (): Promise<void> => {
    const lines = Object.entries(values)
      .filter(([, value]) => value.trim() !== '')
      .map(([materialTypeId, value]) => ({ materialTypeId, kg: Number(value.replace(',', '.')) }));

    const hasProducts = Object.values(productValues).some((value) => value.trim() !== '');
    if (lines.length === 0 && !hasProducts) {
      setError('Заполните хотя бы одну строку');
      return;
    }
    if (lines.some((line) => !Number.isFinite(line.kg) || line.kg < 0)) {
      setError('Количество — неотрицательное число килограммов');
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const body =
        mode === 'opening'
          ? {
              at,
              lines: lines.map((line) => ({
                materialTypeId: line.materialTypeId,
                qtyG: kgToG(line.kg),
              })),
            }
          : {
              at,
              note: note.trim() === '' ? undefined : note.trim(),
              lines: lines.map((line) => ({
                materialTypeId: line.materialTypeId,
                countedQtyG: kgToG(line.kg),
              })),
              // Готовую продукцию считают в тот же заход: по складу ходят один раз
              productLines: Object.entries(productValues)
                .filter(([, value]) => value.trim() !== '')
                .map(([productSizeId, value]) => ({
                  productSizeId,
                  countedQtyPieces: Number(value),
                })),
            };

      await request<unknown>(
        mode === 'opening' ? '/api/stock/opening' : '/api/stock/inventory',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
        30_000,
      );

      await load();
      setMode('none');
      setNotice(
        mode === 'opening' ? 'Первичные остатки записаны' : 'Инвентаризация записана',
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не получилось записать');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Остатки сырья</h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      {error && <p className="mb-4 rounded-xl bg-rose-50 text-rose-800 ring-1 ring-rose-200 px-4 py-3 text-base">{error}</p>}
      {notice && (
        <p className="mb-4 rounded-xl bg-muted px-4 py-3 text-base text-body">{notice}</p>
      )}

      {mode === 'none' && (
        <>
          <div className="mb-6 flex flex-col gap-3">
            {(data?.stock ?? []).map((row) => (
              <article key={row.materialTypeId} className="rounded-xl bg-card shadow-card p-4">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-lg font-semibold">{row.name}</p>
                  <p className="text-lg tabular-nums">{formatKg(row.qtyG)} кг</p>
                </div>
                <p className="mt-1 text-base text-dim">
                  {row.openingAt
                    ? `Первичный остаток на ${row.openingAt}: ${formatKg(row.openingQtyG ?? 0)} кг`
                    : 'Первичный остаток не вводился'}
                </p>
              </article>
            ))}
            {data !== null && data.stock.length === 0 && (
              <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">
                Справочник сырья пуст.
              </p>
            )}
          </div>

          <div className="mb-8 flex flex-col gap-touch">
            <button
              type="button"
              onClick={() => start('opening')}
              className="rounded-xl bg-card shadow-card px-5 py-4 text-lg font-semibold active:bg-line"
            >
              Ввести первичные остатки
            </button>
            <button
              type="button"
              onClick={() => start('inventory')}
              className="rounded-xl bg-sky-700 px-5 py-4 text-lg font-semibold active:bg-sky-800"
            >
              Скорректировать инвентаризацией
            </button>
          </div>

          <h2 className="mb-3 text-lg font-semibold">Инвентаризации</h2>
          <div className="flex flex-col gap-3">
            {(data?.inventories ?? []).length === 0 && (
              <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">
                Пересчётов ещё не было.
              </p>
            )}
            {(data?.inventories ?? []).map((record) => (
              <article key={record.id} className="rounded-xl bg-card shadow-card p-4">
                <p className="text-base font-semibold">
                  {record.at} · {record.byUserName}
                </p>
                {record.note && <p className="text-base text-dim">{record.note}</p>}
                <ul className="mt-2 flex flex-col gap-1">
                  {record.lines.map((line) => (
                    <li key={line.materialTypeId} className="text-base text-body">
                      {line.name}: насчитали {formatKg(line.countedQtyG)}, по системе{' '}
                      {formatKg(line.systemQtyG)}{' '}
                      <span className={line.deltaG === 0 ? 'text-faint' : 'text-amber-700'}>
                        ({line.deltaG > 0 ? '+' : ''}
                        {formatKg(line.deltaG)} кг)
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </>
      )}

      {mode !== 'none' && (
        <section className="rounded-xl bg-card shadow-card p-4">
          <h2 className="mb-1 text-lg font-semibold">
            {mode === 'opening' ? 'Первичные остатки' : 'Инвентаризация'}
          </h2>
          <p className="mb-4 text-base text-dim">
            {mode === 'opening'
              ? 'С этих чисел начинается учёт. Повторный ввод заменит прежние, а приход и расход останутся.'
              : 'Впишите то, что насчитали руками. Разница уйдёт в движения — будет видно, где и на сколько разошлось.'}
          </p>

          <label className="mb-4 flex flex-col gap-1 text-base text-dim">
            На какое число
            <input
              value={at}
              onChange={(event) => setAt(event.target.value)}
              type="date"
              className="rounded-xl bg-muted px-4 py-3 text-base text-strong"
            />
          </label>

          {mode === 'inventory' && (
            <label className="mb-4 flex flex-col gap-1 text-base text-dim">
              Комментарий (необязательно)
              <input
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Например: плановый пересчёт по итогам месяца"
                className="rounded-xl bg-muted px-4 py-3 text-base text-strong"
              />
            </label>
          )}

          <div className="flex flex-col gap-3">
            {(data?.stock ?? []).map((row) => (
              <label key={row.materialTypeId} className="flex items-center gap-3">
                <span className="flex-1 text-base">{row.name}</span>
                <input
                  value={values[row.materialTypeId] ?? ''}
                  onChange={(event) =>
                    setValues({ ...values, [row.materialTypeId]: event.target.value })
                  }
                  inputMode="decimal"
                  placeholder="кг"
                  className="w-32 rounded-xl bg-muted px-4 py-3 text-base tabular-nums"
                />
              </label>
            ))}
          </div>

          {/* Готовую продукцию пересчитывают в тот же заход: по складу ходят
              один раз, и разделять документ незачем */}
          {mode === 'inventory' && products.length > 0 && (
            <div className="mt-6">
              <p className="mb-3 text-base text-dim">Готовая продукция, штук:</p>
              <div className="flex flex-col gap-3">
                {products.map((size) => (
                  <label key={size.id} className="flex items-center gap-3">
                    <span className="flex-1 text-base">{size.name}</span>
                    <input
                      value={productValues[size.id] ?? ''}
                      onChange={(event) =>
                        setProductValues({
                          ...productValues,
                          [size.id]: event.target.value.replace(/\D/g, ''),
                        })
                      }
                      inputMode="numeric"
                      placeholder="шт"
                      className="w-32 rounded-xl bg-muted px-4 py-3 text-base tabular-nums"
                    />
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 flex gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => void submit()}
              className="flex-1 rounded-xl bg-sky-700 px-5 py-4 text-lg font-semibold disabled:opacity-50"
            >
              {busy ? 'Записываю…' : 'Записать'}
            </button>
            <button
              type="button"
              onClick={() => setMode('none')}
              className="rounded-xl bg-muted px-5 py-4 text-lg font-semibold"
            >
              Отмена
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
